import { Color4, CreatePlaneVertexData, Matrix, Mesh, Node, Nullable, Scene, Texture, Vector2 } from '@babylonjs/core';

import { SdfFontSimpleMaterial, SdfTextParagraph, SdfTextParagraphOptions } from '..';

import { SdfFontAsset } from './SdfFontAsset';
import { SdfFontStyle } from './SdfFontStyle';

export type SdfText = {
  text: string;
  font: SdfFontAsset;
  style: SdfFontStyle;
};

export type SdfTextInstance = {};

export type SdfTextMeshOptions = {
  font: SdfFontAsset;
  style?: SdfFontStyle;
};

export class SdfTextMesh extends Mesh {
  private static readonly vertexData = CreatePlaneVertexData({
    size: 1,
  });

  private dirty = false;

  private charMatrices = new Array<number>();
  private charUvs = new Array<number>();

  constructor(
    override readonly name: string,
    scene: Nullable<Scene> = null,
    readonly options: SdfTextMeshOptions
  ) {
    super(name, scene, null, null, false, false);
    SdfTextMesh.vertexData.applyToMesh(this);

    this.material = this.options.font.material;
    this.setEnabled(false);
  }

  override getClassName() {
    return 'SdfTextMesh';
  }

  public addParagraph(paragraph: SdfTextParagraph, matrix = Matrix.Identity(), refresh = true) {
    const fontScale = this.options.font.scale;

    const texWidth = this.options.font.font.common.scaleW;
    const texHeight = this.options.font.font.common.scaleH;
    const glpyhs = paragraph.glyphs.filter((g) => g.char.page >= 0);
    const charUvs = new Float32Array(glpyhs.length * 4);
    const matrices = new Float32Array(glpyhs.length * 16);

    glpyhs.forEach((g, i) => {
      charUvs[i * 4 + 0] = g.char.x / texWidth;
      charUvs[i * 4 + 1] = g.char.y / texHeight;
      charUvs[i * 4 + 2] = g.char.width / texWidth;
      charUvs[i * 4 + 3] = g.char.height / texHeight;

      const x = g.x;
      const y = -g.y;

      const characterScale = Matrix.Scaling(g.char.width, g.char.height, 1.0);
      const baseMatrix = Matrix.Translation(0.5, -0.5, 0).multiply(characterScale);
      const scalingMatrix = Matrix.Scaling(fontScale, fontScale, 1.0);
      const layoutMatrix = Matrix.Translation(x * fontScale, y * fontScale, 0.0);
      const scaled = baseMatrix.multiply(scalingMatrix);
      const relative = scaled.multiply(layoutMatrix);
      const final = relative.multiply(matrix);
      final.copyToArray(matrices, i * 16);
    });

    this.charUvs.push(...charUvs);
    this.charMatrices.push(...matrices);

    this.dirty = true;
    if (refresh) this.refresh();
  }

  public addText(
    text: string,
    paragraphOptions?: Partial<SdfTextParagraphOptions>,
    matrix = Matrix.Identity(),
    refresh = true
  ) {
    const paragraph = new SdfTextParagraph(text, this.options.font, paragraphOptions);

    this.addParagraph(paragraph, matrix, refresh);
  }

  public refresh(force = false) {
    if (!this.dirty && !force) return;

    this.thinInstanceSetBuffer('matrix', Float32Array.from(this.charMatrices), 16);
    this.thinInstanceSetBuffer('charUv', Float32Array.from(this.charUvs), 4);

    this.dirty = false;

    this.setEnabled(this.thinInstanceCount > 0);
  }

  override clone(
    name: string = this.name,
    newParent: Nullable<Node> = this.parent,
    doNotCloneChildren: boolean = true,
    clonePhysicsImpostor: boolean = false
  ) {
    const cloned = new SdfTextMesh(name, this._scene, this.options);
    cloned.parent = newParent;
    return cloned;
  }
}
